import { useCallback, useEffect } from 'react'
import { Heading } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import { UIButton } from 'src/components/ui/Button'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useDailyClaimWithTurnstile } from 'src/services/helpers/dailyClaim'
import { getLocale } from 'src/stores/locale'
import { track, useTrackPageView } from 'src/utils/tracker'
import { ImageWithLoadAnimation } from './ImageWithLoadAnimation'

export function NewUserDailyRewardsDialogContent(props: {
  onComplete: () => void
}) {
  const { t, i18n } = useTranslation(['common', 'user'])
  const { TurnstileWidget, available, claim } = useDailyClaimWithTurnstile()

  const claimDailyQuestAndGoToNextStep = useCallback(async () => {
    if (available) {
      await claim()
    }
    props.onComplete()
  }, [available, claim, props])

  const trackPageView = useTrackPageView()
  useEffect(() => {
    trackPageView({ scene: 'onboarding', section: 'daily-reward', modal: true })
  }, [])

  return (
    <div
      id="new-user-dialog"
      className="flex flex-col gap-4 p-6 w-96 max-w-[calc(100vw-32px)] text-center"
    >
      <Heading className="text-xl font-bold">
        {t('user:onboarding.daily-rewards.title')}
      </Heading>
      <ImageWithLoadAnimation
        src={t('user:onboarding.daily-rewards.figure.src')}
        alt="Daily rewards"
        wrapperClassName="flex-none mx-auto max-w-full w-36 aspect-square rounded-lg"
        className="object-scale-down object-center"
      />
      <p className="text-3xl font-bold">
        <GameIconsCrystalBars className="inline mr-1 text-theme-primary" />
        <Text
          i18n={i18n}
          i18nKey="user:onboarding.daily-rewards.rewards"
          values={{ amount: (10000).toLocaleString(getLocale()) }}
        />
      </p>
      <p className="text-neutral-600 dark:text-neutral-400 text-sm">
        <Text
          i18n={i18n}
          i18nKey="user:onboarding.daily-rewards.info"
          values={{
            nextAmount: (10000).toLocaleString(getLocale()),
          }}
        />
      </p>
      <TurnstileWidget />
      <UIButton
        variant="filled"
        color="theme"
        size="large"
        busy="auto"
        onPress={claimDailyQuestAndGoToNextStep}
      >
        {t('user:onboarding.daily-rewards.continue.label')}
      </UIButton>
    </div>
  )
}
export async function openNewUserDailyRewardsDialog() {
  track('getting_started_show')
  return UIDialog.create({
    dismissable: false,
    className: 'min-w-[unset] max-w-[unset] overflow-clip',
    children: ({ close }) => (
      <NewUserDailyRewardsDialogContent onComplete={close} />
    ),
  })
}
